import jobServices from 'ApiService/Admin/jobServices';
import branchService from 'ApiService/SuperAdmin/branchService';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const StudentProfileCard = ({
  selectedStudent,
  BASE_URL,
  handleFileClick,
  setShowFirstConfirmation,
  setIsEditing,
  getStudents,
}) => {
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showPauseConfirmation, setShowPauseConfirmation] = useState(false);
  const [showUnpauseModal, setShowUnpauseModal] = useState(false);
  const [unpauseReason, setUnpauseReason] = useState('');
  const [isPaused, setIsPaused] = useState(
    selectedStudent.isPlacementPaused ||
      selectedStudent.isPlacementPausedByAdmin
  );
  const [pauseDetails, setPauseDetails] = useState({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const unpauseStatus = selectedStudent.unpauseRequestStatus || 'none';
  const isSuperAdmin = state?.user?.role === 'superadmin';
  const latestPauseReason =
    selectedStudent.pausePlacements?.[selectedStudent.pausePlacements.length - 1]
      ?.reason || selectedStudent.pauseInfo?.reason;
  // Super admins, and admins for auto-declined pauses, unpause without approval
  const canUnpauseDirectly =
    isSuperAdmin || !!latestPauseReason?.startsWith('Auto Declined');

  useEffect(() => {
    setIsPaused(
      selectedStudent.isPlacementPaused ||
        selectedStudent.isPlacementPausedByAdmin
    );
  }, [selectedStudent]);

  const handlePausePlacement = async () => {
    if (
      !pauseDetails.startDate ||
      !pauseDetails.endDate ||
      !pauseDetails.reason
    ) {
      toast.error('Please fill in all fields.');
      return;
    }

    setShowPauseModal(false);
    setShowPauseConfirmation(true);
  };

  const confirmPausePlacement = async () => {
    setIsLoading(true);
    try {
      const response = await jobServices.pauseStudentPlacement({
        ...pauseDetails,
        studentId: selectedStudent._id,
      });

      if (response.data.success) {
        toast.success('Placement paused successfully');
        setShowPauseConfirmation(false);
        setPauseDetails({ startDate: '', endDate: '', reason: '' });
        setIsPaused(true);
        if (getStudents) getStudents();
      } else {
        toast.error(response.data.message || 'Failed to pause placement');
      }
    } catch (error) {
      toast.error('Failed to pause placement');
    } finally {
      setIsLoading(false);
    }
  };

  const submitUnpauseRequest = async () => {
    if (!unpauseReason.trim()) {
      toast.error('Please provide a reason for unpausing placement');
      return;
    }

    setIsLoading(true);
    try {
      const response = isSuperAdmin
        ? await branchService.unpausePlacement(
            selectedStudent._id,
            unpauseReason.trim()
          )
        : canUnpauseDirectly
        ? await jobServices.unpausePlacement(
            selectedStudent._id,
            unpauseReason.trim()
          )
        : await jobServices.requestUnpausePlacement(
            selectedStudent._id,
            unpauseReason.trim()
          );

      if (response.data.success) {
        toast.success(
          response.data.message || 'Unpause request submitted successfully'
        );
        setShowUnpauseModal(false);
        setUnpauseReason('');
        if (canUnpauseDirectly) setIsPaused(false);
        if (getStudents) getStudents();
      } else {
        toast.error(
          response.data.message || 'Failed to submit unpause request'
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to submit unpause request'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnpausePlacement = async () => {
    setIsLoading(true);
    try {
      const response = await jobServices.unpausePlacement(selectedStudent._id);
      if (response.data.success) {
        toast.success('Placement unpaused successfully');
        setIsPaused(false);
        if (getStudents) getStudents();
      } else {
        toast.error(response.data.message || 'Failed to unpause placement');
      }
    } catch (error) {
      toast.error('Failed to unpause placement');
    } finally {
      setIsLoading(false);
    }
  };

  const pauseInfo = selectedStudent.pauseInfo || {};
  const isPermanentlyPaused = selectedStudent.pausePlacements?.some(
    (pause) => pause.isPermanent === true
  );

  const mostRecentPause =
    selectedStudent.pausePlacements?.length > 0
      ? selectedStudent.pausePlacements[
          selectedStudent.pausePlacements.length - 1
        ]
      : null;

  return (
    <div className="w-full rounded-lg border border-double border-brand-500 bg-white p-6 shadow-md dark:bg-navy-900 md:w-1/3">
      <div className="mb-4 flex justify-center">
        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gray-300">
          {selectedStudent.profilePicture ? (
            <div
              className="h-32 w-32 cursor-pointer"
              onClick={() =>
                handleFileClick({
                  type: 'image',
                  src: `${BASE_URL}/${selectedStudent.profilePicture}`,
                  filename: 'Profile Image',
                })
              }
            >
              <img
                src={`${BASE_URL}/${selectedStudent.profilePicture}`}
                alt="Profile"
                className="h-full w-full rounded-full object-cover"
              />
            </div>
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gray-300">
              <span className="text-gray-600">No Image</span>
            </div>
          )}
        </div>
      </div>
      <h2 className="mb-2 text-center text-xl font-bold">
        {selectedStudent.name}
      </h2>
      <p className="mb-1 text-center">Email: {selectedStudent.email}</p>
      <p className="mb-1 text-center">
        Contact: {selectedStudent.contactNumber}
      </p>

      {/* Display pause dates and reason if paused */}
      {isPaused && pauseInfo.startDate && pauseInfo.endDate && (
        <div className="mt-2 text-center text-sm text-gray-600">
          <p>
            Paused: {new Date(pauseInfo.startDate).toLocaleDateString()} to{' '}
            {new Date(pauseInfo.endDate).toLocaleDateString()}
          </p>
          {pauseInfo.reason && (
            <p className="italic">Reason: {pauseInfo.reason}</p>
          )}
        </div>
      )}

      {/* Unpause status indicator */}
      {isPaused && unpauseStatus !== 'none' && (
        <div className="mt-2 flex justify-center">
          {unpauseStatus === 'pending' ? (
            <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
              Unpause Request Pending Approval
            </span>
          ) : unpauseStatus === 'rejected' ? (
            <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">
              Unpause Request Rejected
            </span>
          ) : null}
        </div>
      )}

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button
          onClick={() => setShowFirstConfirmation(true)}
          className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
        >
          Deactivate Student
        </button>
        <button
          onClick={() => setIsEditing(true)}
          className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
        >
          Edit Student
        </button>

        {isPermanentlyPaused ? (
          <div className="flex flex-col items-center">
            <button
              // disabled={true}
              onClick={() => {
                if (state?.user?.role === 'superadmin') {
                  navigate('/superadmin/pause/students/placement');
                } else if (state?.user?.role === 'admin') {
                  navigate('/admin/pause/students/placement');
                }
              }}
              className="cursor-not-allowed rounded bg-red-500 px-4 py-2 text-white opacity-75"
            >
              Permanently Paused
            </button>
          </div>
        ) : isPaused ? (
          canUnpauseDirectly ? (
            <button
              onClick={() => setShowUnpauseModal(true)}
              disabled={isLoading}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
            >
              {isLoading ? 'Loading...' : 'Unpause Placement'}
            </button>
          ) : unpauseStatus === 'pending' ? (
            <button
              disabled={true}
              className="cursor-not-allowed rounded bg-yellow-500 px-4 py-2 text-white opacity-75"
            >
              Unpause Pending
            </button>
          ) : unpauseStatus === 'approved' ? (
            <button
              onClick={handleUnpausePlacement}
              disabled={isLoading}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
            >
              {isLoading ? 'Loading...' : 'Unpause Placement'}
            </button>
          ) : (
            <button
              onClick={() => setShowUnpauseModal(true)}
              disabled={isLoading}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
            >
              {unpauseStatus === 'rejected'
                ? 'Retry Unpause Request'
                : 'Request Unpause'}
            </button>
          )
        ) : (
          <button
            onClick={() => setShowPauseModal(true)}
            disabled={isLoading}
            className="rounded bg-yellow-500 px-4 py-2 text-white hover:bg-yellow-600"
          >
            {isLoading ? 'Loading...' : 'Pause Placement'}
          </button>
        )}
      </div>

      {/* Pause Details Modal */}
      {showPauseModal && (
        <div className="bg-black fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 p-2">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <button
              onClick={() => setShowPauseModal(false)}
              className="absolute right-2 top-2 text-2xl text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="mb-4 text-lg font-semibold">Pause Placement</h2>
            <div className="space-y-4">
              <div>
                <InputField
                  label="Start Date"
                  type="date"
                  value={pauseDetails.startDate}
                  onChange={(e) =>
                    setPauseDetails({
                      ...pauseDetails,
                      startDate: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-navy-800"
                />
              </div>
              <div>
                <InputField
                  label="End Date"
                  type="date"
                  value={pauseDetails.endDate}
                  min={pauseDetails.startDate}
                  onChange={(e) =>
                    setPauseDetails({
                      ...pauseDetails,
                      endDate: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-navy-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reason
                </label>
                <textarea
                  value={pauseDetails.reason}
                  onChange={(e) =>
                    setPauseDetails({ ...pauseDetails, reason: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-navy-800"
                  rows="3"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={handlePausePlacement}
                disabled={isLoading}
                className="w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                {isLoading ? 'Loading...' : 'Pause'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpause Request Modal - New */}
      {showUnpauseModal && (
        <div className="bg-black fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 p-2">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <button
              onClick={() => setShowUnpauseModal(false)}
              className="absolute right-2 top-2 text-2xl text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
            <h2 className="mb-4 text-lg font-semibold">
              {canUnpauseDirectly ? 'Unpause' : 'Request Unpause for'}{' '}
              {selectedStudent.name}
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason for Unpause
              </label>
              <textarea
                value={unpauseReason}
                onChange={(e) => setUnpauseReason(e.target.value)}
                placeholder={
                  canUnpauseDirectly
                    ? 'Enter reason for unpausing'
                    : 'Enter reason for requesting unpause'
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-navy-800"
                rows="3"
              />
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={submitUnpauseRequest}
                disabled={isLoading}
                className="w-40 rounded-xl bg-green-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
              >
                {isLoading
                  ? 'Submitting...'
                  : canUnpauseDirectly
                  ? 'Unpause'
                  : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showPauseConfirmation && (
        <div className="bg-black fixed inset-0 flex items-center justify-center bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-4 shadow-lg dark:bg-navy-800 md:mx-0 md:p-8">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 rounded-full border border-yellow-100 bg-yellow-50 p-2 text-yellow-400 md:h-16 md:w-16 md:p-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="ml-3 flex-1 md:ml-4">
                <h2 className="text-base font-medium dark:text-white md:text-lg">
                  Pause Student Placement?
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to pause this student's placement from{' '}
                  {pauseDetails.startDate} to {pauseDetails.endDate}?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={confirmPausePlacement}
                disabled={isLoading}
                className="ml-3 rounded-full border-2 border-yellow-500 bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-yellow-600 hover:shadow-lg"
              >
                {isLoading ? 'Processing...' : 'Confirm Pause'}
              </button>
              <button
                onClick={() => setShowPauseConfirmation(false)}
                className="ml-3 rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition duration-200 hover:bg-gray-100 hover:shadow-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfileCard;
